import { useEffect, useMemo, useRef } from 'react';
import { UseFormReturn } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Plus } from 'lucide-react';

import { FormActionButtons } from '@/components/layout/form-action-buttons';
import { FormHeaderWithStatus } from '@/components/layout/form-header-with-status';
import { Card } from '@/components/ui/card';
import { CustomerAutocomplete } from '@/components/ui/customer-autocomplete';
import { FieldList } from '@/components/ui/field-list';
import {
	Form,
	FormControl,
	FormDescription,
	FormField,
	FormItem,
	FormLabel,
	FormMessage
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { FormSwitch } from '@/components/ui/form-switch';
import { SentryDropdown } from '@/components/ui/sentry-dropdown';
import { Separator } from '@/components/ui/separator';
import { Textarea } from '@/components/ui/textarea';
import {
	VehicleMakeAutocomplete,
	VehicleModelAutocomplete
} from '@/components/ui/vehicle-make-model-autocomplete';

export interface SelectOption {
	value: string;
	label: string;
	description?: string | null;
}

export interface CustomerContactFormValue {
	customerContactId?: string;
	contactTypeId?: string;
	contactValue?: string;
	isPrimary: boolean;
	notes?: string;
	isDeleted?: boolean;
}

export interface CustomerAddressFormValue {
	customerAddressId?: string;
	addressId?: string;
	addressTypeId?: string;
	streetAddress?: string;
	city?: string;
	state?: string;
	postalCode?: string;
	country?: string;
	isDeleted?: boolean;
}

export interface CustomerBankDetailFormValue {
	bankDetailsId?: string;
	bankName?: string;
	accountNumber?: string;
	iban?: string;
	bankCode?: string;
	accountHolderName?: string;
	isPrimary: boolean;
	isDeleted?: boolean;
}

export interface CustomerBeneficiaryFormValue {
	customerBeneficiaryId?: string;
	beneficiaryCustomerId?: string;
	beneficiaryDisplayName?: string; // Display name for existing beneficiaries
	isActive: boolean;
	notes?: string;
	relationshipType?: string;
	isDeleted?: boolean;
}

export interface CustomerVehicleFormValue {
	vehicleId?: string;
	plateNumber?: string;
	vehicleTypeId?: string;
	make?: string;
	model?: string;
	color?: string;
	isActive: boolean;
	isDeleted?: boolean;
}

export interface CustomerFormValues {
	isCompany: boolean;
	firmName?: string;
	firstName?: string;
	lastName?: string;
	email?: string;
	fiscalNumber?: string;
	marketingOptIn: boolean;
	notes?: string;
	isActive: boolean;
	customerCategoryId: string;
	preferredLanguage?: string;
	companyRegistrationNumber?: string;
	industry?: string;
	companySize?: string;
	contacts: CustomerContactFormValue[];
	addresses: CustomerAddressFormValue[];
	bankDetails: CustomerBankDetailFormValue[];
	beneficiaries: CustomerBeneficiaryFormValue[];
	vehicles: CustomerVehicleFormValue[];
}

interface CustomerFormProps {
	mode: 'create' | 'edit';
	form: UseFormReturn<CustomerFormValues>;
	customerCategoryOptions: SelectOption[];
	contactTypeOptions: SelectOption[];
	addressTypeOptions: SelectOption[];
	vehicleTypeOptions: SelectOption[];
	isSubmitting: boolean;
	isLoadingLookups?: boolean;
	currentCustomerId?: string;
	onSubmit: (values: CustomerFormValues) => Promise<void> | void;
	headerTitle: string;
	onBack?: () => void;
}

const hasLookupData = (options: SelectOption[]) =>
	options && options.length > 0;

export const CustomerForm = ({
	mode,
	form,
	customerCategoryOptions,
	contactTypeOptions,
	addressTypeOptions,
	vehicleTypeOptions,
	isSubmitting,
	isLoadingLookups = false,
	currentCustomerId,
	onSubmit,
	headerTitle,
	onBack
}: CustomerFormProps) => {
	const { t } = useTranslation('customers');
	const isCompany = form.watch('isCompany');
	const firstNameInputRef = useRef<HTMLInputElement>(null);

	// Find the "Car" vehicle type to use as default when adding new vehicles
	const defaultVehicleTypeId = useMemo(() => {
		const carOption = vehicleTypeOptions.find(
			option => option.label.toLowerCase() === 'car'
		);
		return carOption?.value ?? '';
	}, [vehicleTypeOptions]);

	// Auto-focus first name field when creating a new customer (individual)
	useEffect(() => {
		if (mode === 'create' && !isCompany) {
			// Use setTimeout to ensure the DOM is ready
			setTimeout(() => {
				firstNameInputRef.current?.focus();
			}, 0);
		}
	}, [mode, isCompany]);

	return (
		<>
			<FormHeaderWithStatus
				title={headerTitle}
				onBack={onBack}
				control={form.control}
				rightContent={
					<FormActionButtons
						mode={mode}
						isSubmitting={isSubmitting}
						disabled={
							isSubmitting ||
							isLoadingLookups ||
							!hasLookupData(customerCategoryOptions)
						}
						validationErrors={form.formState.errors}
						formId="customer-form"
					/>
				}
			/>
			<Form {...form}>
				<form id="customer-form" onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
				<Card className="space-y-6">
					<div className="space-y-1">
						<h2 className="text-lg font-semibold">
							{t('form.sections.profile.title')}
						</h2>
						<p className="text-sm text-muted-foreground">
							{t('form.sections.profile.description')}
						</p>
					</div>

					<FormSwitch
						control={form.control}
						name="marketingOptIn"
						variant="inline"
						label={t('fields.marketingOptIn')}
						enabledText={t('form.marketing.subscribed')}
						disabledText={t('form.marketing.unsubscribed')}
						statusTextWidth="w-24"
					/>

					<Separator className="my-2" />

					<div className="grid gap-6 grid-cols-1 md:grid-cols-2 lg:grid-cols-3">
						<FormField
							control={form.control}
							name="isCompany"
							render={({ field }) => (
								<FormItem>
									<FormLabel>
										{t('form.customerType.label')}
									</FormLabel>
									<FormControl>
										<SentryDropdown
											options={[
												{
													value: 'false',
													label: t('form.customerType.individual')
												},
												{
													value: 'true',
													label: t('form.customerType.company')
												}
											]}
											value={field.value ? 'true' : 'false'}
											onChange={(value) => {
												const isCompanyValue = value === 'true';
												field.onChange(isCompanyValue);

												// Reset fields based on customer type
												if (isCompanyValue) {
													// Switching to company - reset individual fields
													form.setValue('firstName', '');
													form.setValue('lastName', '');
												} else {
													// Switching to individual - reset company fields and beneficiaries
													form.setValue('firmName', '');
													form.setValue('beneficiaries', []);
												}
											}}
											placeholder={t('form.customerType.label')}
											groupByCategory={false}
											disabled={mode === 'edit'}
										/>
									</FormControl>
									<FormMessage />
								</FormItem>
							)}
						/>

						<FormField
							control={form.control}
							name="customerCategoryId"
							rules={{
								required: t(
									'validationMessages.customerCategoryRequired'
								)
							}}
							render={({ field }) => (
								<FormItem>
									<FormLabel>
										{t('fields.customerCategory')} *
									</FormLabel>
									<FormControl>
										<SentryDropdown
											options={customerCategoryOptions.map(
												option => ({
													value: option.value,
													label: option.description
														? `${option.label} - ${option.description}`
														: option.label
												})
											)}
											value={field.value || ''}
											onChange={value =>
												field.onChange(value)
											}
											placeholder={t(
												'placeholders.selectCategory'
											)}
											disabled={
												!hasLookupData(
													customerCategoryOptions
												)
											}
											searchable
											className="w-full"
											autoSelectFirstOption={true}
										/>
									</FormControl>
									<FormMessage />
								</FormItem>
							)}
						/>
					</div>

					{isCompany && (
						<>
							<div className="grid gap-6 grid-cols-1 md:grid-cols-2 lg:grid-cols-3">
								<FormField
									control={form.control}
									name="firmName"
									rules={{
										validate: (value, formValues) => {
											if (!formValues.isCompany) {
												return true; // Not required for individuals
											}
											return (
												Boolean(value?.trim()) ||
												t(
													'validationMessages.companyNameRequired'
												)
											);
										}
									}}
									render={({ field }) => (
										<FormItem>
											<FormLabel>
												{t('fields.companyName')} *
											</FormLabel>
											<FormControl>
												<Input
													{...field}
													placeholder={t(
														'placeholders.companyName'
													)}
												/>
											</FormControl>
											<FormMessage />
										</FormItem>
									)}
								/>
								<FormField
									control={form.control}
									name="industry"
									render={({ field }) => (
										<FormItem>
											<FormLabel>
												{t('fields.industry')}
											</FormLabel>
											<FormControl>
												<Input
													{...field}
													placeholder={t(
														'placeholders.industry'
													)}
												/>
											</FormControl>
											<FormMessage />
										</FormItem>
									)}
								/>
								<FormField
									control={form.control}
									name="companyRegistrationNumber"
									render={({ field }) => (
										<FormItem>
											<FormLabel>
												{t('fields.registrationNumber')}
											</FormLabel>
											<FormControl>
												<Input
													{...field}
													placeholder={t(
														'placeholders.businessRegistration'
													)}
												/>
											</FormControl>
											<FormMessage />
										</FormItem>
									)}
								/>
								<FormField
									control={form.control}
									name="companySize"
									render={({ field }) => (
										<FormItem>
											<FormLabel>
												{t('fields.companySize')}
											</FormLabel>
											<FormControl>
												<Input
													{...field}
													placeholder={t(
														'placeholders.companySize'
													)}
												/>
											</FormControl>
											<FormMessage />
										</FormItem>
									)}
								/>
							</div>
						</>
					)}

					{!isCompany && (
						<div className="grid gap-6 grid-cols-1 md:grid-cols-2 lg:grid-cols-3">
							<FormField
								control={form.control}
								name="firstName"
								rules={{
									validate: (value, formValues) => {
										if (formValues.isCompany) {
											return true; // Not required for companies
										}
										return (
											Boolean(value?.trim()) ||
											t(
												'validationMessages.firstNameRequired'
											)
										);
									}
								}}
								render={({ field }) => (
									<FormItem>
										<FormLabel>
											{t('fields.firstName')} *
										</FormLabel>
										<FormControl>
											<Input
												{...field}
												ref={firstNameInputRef}
												placeholder={t(
													'placeholders.firstName'
												)}
											/>
										</FormControl>
										<FormMessage />
									</FormItem>
								)}
							/>
							<FormField
								control={form.control}
								name="lastName"
								rules={{
									validate: (value, formValues) => {
										if (formValues.isCompany) {
											return true; // Not required for companies
										}
										return (
											Boolean(value?.trim()) ||
											t(
												'validationMessages.lastNameRequired'
											)
										);
									}
								}}
								render={({ field }) => (
									<FormItem>
										<FormLabel>
											{t('fields.lastName')} *
										</FormLabel>
										<FormControl>
											<Input
												{...field}
												placeholder={t(
													'placeholders.lastName'
												)}
											/>
										</FormControl>
										<FormMessage />
									</FormItem>
								)}
							/>
						</div>
					)}

					<div className="grid gap-6 grid-cols-1 md:grid-cols-2 lg:grid-cols-3">
						<FormField
							control={form.control}
							name="fiscalNumber"
							render={({ field }) => (
								<FormItem>
									<FormLabel>
										{t('fields.fiscalNumber')}
									</FormLabel>
									<FormControl>
										<Input
											{...field}
											placeholder={t(
												'placeholders.fiscalNumber'
											)}
										/>
									</FormControl>
									<FormMessage />
								</FormItem>
							)}
						/>
						<FormField
							control={form.control}
							name="email"
							rules={{
								pattern: {
									value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/u,
									message: t(
										'validationMessages.validEmailRequired'
									)
								}
							}}
							render={({ field }) => (
								<FormItem>
									<FormLabel>{t('fields.email')}</FormLabel>
									<FormControl>
										<Input
											{...field}
											type="email"
											placeholder={t(
												'placeholders.email'
											)}
										/>
									</FormControl>
									<FormMessage />
								</FormItem>
							)}
						/>
						<FormField
							control={form.control}
							name="preferredLanguage"
							render={({ field }) => (
								<FormItem>
									<FormLabel>
										{t('fields.preferredLanguage')}
									</FormLabel>
									<FormControl>
										<SentryDropdown
											value={field.value ?? ''}
											onChange={field.onChange}
											options={[
												{
													value: '',
													label: t(
														'fields.preferredLanguageNotSpecified'
													)
												},
												{
													value: 'EL',
													label: t(
														'fields.preferredLanguageEL'
													)
												},
												{
													value: 'EN',
													label: t(
														'fields.preferredLanguageEN'
													)
												}
											]}
											searchable={false}
										/>
									</FormControl>
									<FormMessage />
								</FormItem>
							)}
						/>
					</div>
					<FormField
						control={form.control}
						name="notes"
						render={({ field }) => (
							<FormItem>
								<FormLabel>
									{t('fields.internalNotes')}
								</FormLabel>
								<FormControl>
									<Textarea
										{...field}
										rows={4}
										placeholder={t(
											'placeholders.internalNotes'
										)}
									/>
								</FormControl>
								<FormMessage />
							</FormItem>
						)}
					/>
				</Card>

                                <div className="space-y-4">
                                        <FieldList
                                                form={form}
                                                name="contacts"
                                                defaultItem={{
                                                        contactTypeId: '',
                                                        contactValue: '',
                                                        isPrimary: false,
                                                        notes: '',
                                                        isDeleted: false
                                                }}
                                                addButtonLabel={t('fields.addNewContact')}
                                                fields={[
							{
								name: 'contactValue',
								isKeyField: true,
								render: ({ form, path, isNewItem }) => (
									<FormField
										control={form.control}
										name={`${path}.contactValue` as any}
										render={({
											field: contactValueField
										}) => (
											<FormItem>
												<FormLabel className="flex items-center gap-2">
													{isNewItem && (
														<Plus className="w-4 h-4 text-muted-foreground" />
													)}
													{isNewItem
														? t(
																'fields.addNewContact'
															)
														: t(
																'fields.contactValue'
															)}
												</FormLabel>
												<FormControl>
													<Input
														{...contactValueField}
														placeholder={
															isNewItem
																? t(
																		'placeholders.addContact'
																	)
																: t(
																		'placeholders.contactValue'
																	)
														}
													/>
												</FormControl>
												<FormMessage />
											</FormItem>
										)}
									/>
								)
							},
							{
								name: 'contactTypeId',
								render: ({
									form,
									path,
									isNewItem: _isNewItem
								}) => (
									<FormField
										control={form.control}
										name={`${path}.contactTypeId` as any}
										rules={{
											required: t(
												'validationMessages.contactTypeRequired'
											)
										}}
										render={({ field: contactField }) => (
											<FormItem>
												<FormLabel>
													{t('fields.contactType')} *
												</FormLabel>
												<FormControl>
													<SentryDropdown
														options={contactTypeOptions.map(
															option => ({
																value: option.value,
																label: option.label
															})
														)}
														value={
															contactField.value ||
															''
														}
														onChange={value =>
															contactField.onChange(
																value
															)
														}
														placeholder={t(
															'placeholders.selectType'
														)}
														disabled={
															!hasLookupData(
																contactTypeOptions
															)
														}
														searchable
														className="w-full"
														autoSelectFirstOption={
															true
														}
													/>
												</FormControl>
												<FormMessage />
											</FormItem>
										)}
									/>
								)
							},
							{
								name: 'isPrimary',
								render: ({
									form,
									path
								}) => (
									<FormSwitch
										control={form.control}
										name={`${path}.isPrimary` as any}
										variant="input"
										label={t('fields.markAsPrimary')}
										enabledText={t('fields.primaryContactEnabled')}
										disabledText={t('fields.primaryContactDisabled')}
									/>
								)
							},
							{
								name: 'notes',
								colSpan: 'col-span-full',
								render: ({
									form,
									path,
									isNewItem: _isNewItem
								}) => (
									<FormField
										control={form.control}
										name={`${path}.notes` as any}
										render={({
											field: contactNotesField
										}) => (
											<FormItem>
												<FormLabel>
													{t('fields.notes')}
												</FormLabel>
												<FormControl>
													<Textarea
														{...contactNotesField}
														rows={2}
														placeholder={t(
															'placeholders.contactNotes'
														)}
													/>
												</FormControl>
												<FormMessage />
											</FormItem>
										)}
									/>
								)
							}
						]}
                                                title={t('form.sections.contacts.title')}
                                                description={t('form.sections.contacts.description')}
                                                emptyMessage={t('form.sections.contacts.emptyMessage')}
                                                idField="customerContactId"
                                                deletedField="isDeleted"
                                        />
                                </div>

                                <div className="space-y-4">
                                        <FieldList
                                                form={form}
                                                name="addresses"
                                                title={t('form.sections.addresses.title')}
                                                description={t('form.sections.addresses.description')}
                                                emptyMessage={t('form.sections.addresses.emptyMessage')}
                                                idField="customerAddressId"
                                                defaultItem={{
                                                        customerAddressId: '',
                                                        addressId: '',
                                                        addressTypeId: '',
                                                        streetAddress: '',
                                                        city: '',
                                                        state: '',
                                                        postalCode: '',
                                                        country: '',
                                                        isDeleted: false
                                                }}
                                                addButtonLabel={t('fields.addNewAddress')}
                                                fields={[
							{
								name: 'streetAddress',
								isKeyField: true,
								render: ({ form, path, isNewItem }) => (
									<FormField
										control={form.control}
										name={`${path}.streetAddress` as any}
										render={({ field }) => (
											<FormItem>
												<FormLabel className="flex items-center gap-2">
													{isNewItem && (
														<Plus className="w-4 h-4 text-muted-foreground" />
													)}
													{isNewItem
														? t(
																'fields.addNewAddress'
															)
														: t(
																'fields.streetAddress'
															)}
												</FormLabel>
												<FormControl>
													<Input
														{...field}
														placeholder={
															isNewItem
																? t(
																		'placeholders.addAddress'
																	)
																: t(
																		'placeholders.streetAddress'
																	)
														}
													/>
												</FormControl>
												<FormMessage />
											</FormItem>
										)}
									/>
								)
							},
							{
								name: 'city',
								render: ({
									form,
									path,
									isNewItem: _isNewItem
								}) => (
									<FormField
										control={form.control}
										name={`${path}.city` as any}
										render={({ field }) => (
											<FormItem>
												<FormLabel>
													{t('fields.city')}
												</FormLabel>
												<FormControl>
													<Input
														{...field}
														placeholder={t(
															'placeholders.city'
														)}
													/>
												</FormControl>
												<FormMessage />
											</FormItem>
										)}
									/>
								)
							},
							{
								name: 'state',
								render: ({
									form,
									path,
									isNewItem: _isNewItem
								}) => (
									<FormField
										control={form.control}
										name={`${path}.state` as any}
										render={({ field }) => (
											<FormItem>
												<FormLabel>
													{t('fields.stateProvince')}
												</FormLabel>
												<FormControl>
													<Input
														{...field}
														placeholder={t(
															'placeholders.stateProvince'
														)}
													/>
												</FormControl>
												<FormMessage />
											</FormItem>
										)}
									/>
								)
							},
							{
								name: 'postalCode',
								render: ({
									form,
									path,
									isNewItem: _isNewItem
								}) => (
									<FormField
										control={form.control}
										name={`${path}.postalCode` as any}
										render={({ field }) => (
											<FormItem>
												<FormLabel>
													{t('fields.postalCode')}
												</FormLabel>
												<FormControl>
													<Input
														{...field}
														placeholder={t(
															'placeholders.postalCode'
														)}
													/>
												</FormControl>
												<FormMessage />
											</FormItem>
										)}
									/>
								)
							},
							{
								name: 'country',
								render: ({
									form,
									path,
									isNewItem: _isNewItem
								}) => (
									<FormField
										control={form.control}
										name={`${path}.country` as any}
										render={({ field }) => (
											<FormItem>
												<FormLabel>
													{t('fields.country')}
												</FormLabel>
												<FormControl>
													<Input
														{...field}
														placeholder={t(
															'placeholders.country'
														)}
													/>
												</FormControl>
												<FormMessage />
											</FormItem>
										)}
									/>
								)
							},
							{
								name: 'addressTypeId',
								colSpan: 'col-span-full',
								render: ({
									form,
									path,
									isNewItem: _isNewItem
								}) => (
									<FormField
										control={form.control}
										name={`${path}.addressTypeId` as any}
										rules={{
											required: t(
												'validationMessages.addressTypeRequired'
											)
										}}
										render={({ field }) => (
											<FormItem>
												<FormLabel>
													{t('fields.addressType')} *
												</FormLabel>
												<FormControl>
													<SentryDropdown
														options={addressTypeOptions.map(
															option => ({
																value: option.value,
																label: option.label
															})
														)}
														value={
															field.value || ''
														}
														onChange={value =>
															field.onChange(
																value
															)
														}
														placeholder={t(
															'placeholders.selectType'
														)}
														disabled={
															!hasLookupData(
																addressTypeOptions
															)
														}
														searchable
														className="w-full"
														autoSelectFirstOption={
															true
														}
													/>
												</FormControl>
												<FormMessage />
											</FormItem>
										)}
									/>
								)
							}
						]}
					/>
				</div>

                                <div className="space-y-4">
                                        <FieldList
                                                form={form}
                                                name="bankDetails"
                                                title={t('form.sections.bankDetails.title')}
                                                description={t('form.sections.bankDetails.description')}
                                                emptyMessage={t(
                                                        'form.sections.bankDetails.emptyMessage'
                                                )}
                                                idField="bankDetailsId"
                                                defaultItem={{
                                                        bankName: '',
                                                        accountNumber: '',
                                                        iban: '',
                                                        bankCode: '',
                                                        accountHolderName: '',
                                                        isPrimary: false,
                                                        isDeleted: false
                                                }}
                                                addButtonLabel={t('fields.addNewBank')}
                                                fields={[
							{
								name: 'bankName',
								isKeyField: true,
								render: ({ form, path, isNewItem }) => (
									<FormField
										control={form.control}
										name={`${path}.bankName` as any}
										render={({ field }) => (
											<FormItem>
												<FormLabel className="flex items-center gap-2">
													{isNewItem && (
														<Plus className="w-4 h-4 text-muted-foreground" />
													)}
													{isNewItem
														? t('fields.addNewBank')
														: t('fields.bankName')}
												</FormLabel>
												<FormControl>
													<Input
														{...field}
														placeholder={
															isNewItem
																? t(
																		'placeholders.addBank'
																	)
																: t(
																		'placeholders.bank'
																	)
														}
													/>
												</FormControl>
												<FormMessage />
											</FormItem>
										)}
									/>
								)
							},
							{
								name: 'accountHolderName',
								render: ({
									form,
									path,
									isNewItem
								}) => (
									<FormField
										control={form.control}
										name={
											`${path}.accountHolderName` as any
										}
										rules={{
											required: isNewItem
												? false
												: t(
														'validationMessages.accountHolderRequired'
													)
										}}
										render={({ field }) => (
											<FormItem>
												<FormLabel>
													{t('fields.accountHolder')}{' '}
													*
												</FormLabel>
												<FormControl>
													<Input
														{...field}
														placeholder={t(
															'placeholders.customerName'
														)}
													/>
												</FormControl>
												<FormMessage />
											</FormItem>
										)}
									/>
								)
							},
							{
								name: 'accountNumber',
								render: ({
									form,
									path,
									isNewItem
								}) => (
									<FormField
										control={form.control}
										name={`${path}.accountNumber` as any}
										rules={{
											required: isNewItem
												? false
												: t(
														'validationMessages.accountNumberRequired'
													)
										}}
										render={({ field }) => (
											<FormItem>
												<FormLabel>
													{t('fields.accountNumber')}{' '}
													*
												</FormLabel>
												<FormControl>
													<Input
														{...field}
														placeholder={t(
															'placeholders.accountNumber'
														)}
													/>
												</FormControl>
												<FormMessage />
											</FormItem>
										)}
									/>
								)
							},
							{
								name: 'iban',
								render: ({
									form,
									path,
									isNewItem: _isNewItem
								}) => (
									<FormField
										control={form.control}
										name={`${path}.iban` as any}
										render={({ field }) => (
											<FormItem>
												<FormLabel>
													{t('fields.iban')}
												</FormLabel>
												<FormControl>
													<Input
														{...field}
														placeholder={t(
															'placeholders.iban'
														)}
													/>
												</FormControl>
												<FormMessage />
											</FormItem>
										)}
									/>
								)
							},
							{
								name: 'bankCode',
								render: ({
									form,
									path,
									isNewItem: _isNewItem
								}) => (
									<FormField
										control={form.control}
										name={`${path}.bankCode` as any}
										render={({ field }) => (
											<FormItem>
												<FormLabel>
													{t('fields.bankCode')}
												</FormLabel>
												<FormControl>
													<Input
														{...field}
														placeholder={t(
															'placeholders.bankCode'
														)}
													/>
												</FormControl>
												<FormMessage />
											</FormItem>
										)}
									/>
								)
							},
{
										name: 'isPrimary',
										render: ({
											form,
											path
										}) => (
											<FormSwitch
												control={form.control}
												name={`${path}.isPrimary` as any}
												variant="input"
												label={t('fields.markAsPrimaryPayout')}
												enabledText={t('fields.primaryPayoutEnabled')}
												disabledText={t('fields.primaryPayoutDisabled')}
											/>
										)
									}
								]}
							/>
						</div>				{isCompany && (
					<div className="space-y-4">
						<div className="flex items-start justify-between">
							<div className="flex flex-col gap-2">
								<h3 className="text-lg font-semibold">
									{t('form.sections.beneficiaries.title')}
								</h3>
								<p className="text-sm text-muted-foreground">
									{t('form.sections.beneficiaries.description')}
								</p>
							</div>
						</div>
						<FieldList
							form={form}
							name="beneficiaries"
							title=""
                                                        description=""
                                                        emptyMessage={t(
                                                                'form.sections.beneficiaries.emptyMessage'
                                                        )}
                                                        idField="customerBeneficiaryId"
							defaultItem={{
								beneficiaryCustomerId: '',
								beneficiaryDisplayName: '',
								isActive: true,
								notes: '',
								relationshipType: '',
								isDeleted: false
							}}
                                                        addButtonLabel={t('fields.addNewBeneficiary')}
                                                        renderHeaderActions={({ form, path }) => (
								<FormSwitch
									control={form.control}
									name={`${path}.isActive` as any}
									variant="compact"
									label={t('fields.active')}
								/>
							)}
							fields={[
								{
									name: 'beneficiaryCustomerId',
									isKeyField: true,
									render: ({ form, path, isNewItem, index }) => {
										// Get all beneficiaries and extract already-selected customer IDs
										// excluding the current item's selection
										const beneficiaries = form.watch('beneficiaries') as CustomerBeneficiaryFormValue[];
										const excludeCustomerIds = beneficiaries
											.filter((_, i) => i !== index) // Exclude current item
											.map(b => b.beneficiaryCustomerId)
											.filter((id): id is string => Boolean(id && id.trim()));

										return (
										<FormField
											control={form.control}
											name={
												`${path}.beneficiaryCustomerId` as any
											}
											render={({ field }) => (
												<FormItem>
													<FormLabel className="flex items-center gap-2">
														{isNewItem && (
															<Plus className="w-4 h-4 text-muted-foreground" />
														)}
														{isNewItem
															? t(
																	'fields.addNewBeneficiary'
																)
															: t(
																	'fields.beneficiaryCustomer'
																)}
													</FormLabel>
													<FormControl>
														<CustomerAutocomplete
															value={field.value}
															onChange={(id, customer) => {
																field.onChange(id);
																form.setValue(
																	`${path}.beneficiaryDisplayName` as any,
																	customer?.displayName ?? '',
																	{ shouldDirty: true }
																);
															}}
															placeholder={
																isNewItem
																	? t(
																			'placeholders.selectBeneficiary'
																		)
																	: t(
																			'placeholders.beneficiaryCustomer'
																		)
															}
															initialSelectedLabel={
																form.getValues(
																	`${path}.beneficiaryDisplayName` as any
																)
															}
															excludeCustomerId={
																currentCustomerId
															}
															excludeCustomerIds={
																excludeCustomerIds
															}
														/>
													</FormControl>
													<FormDescription>
														{isNewItem
															? t(
																	'descriptions.chooseBeneficiary'
																)
															: t(
																	'descriptions.selectBeneficiary'
																)}
													</FormDescription>
													<FormMessage />
												</FormItem>
											)}
										/>
									);
									}
								},
								{
									name: 'relationshipType',
									render: ({
										form,
										path,
										isNewItem: _isNewItem
									}) => (
										<FormField
											control={form.control}
											name={
												`${path}.relationshipType` as any
											}
											render={({ field }) => (
												<FormItem>
													<FormLabel>
														{t(
															'fields.relationshipType'
														)}
													</FormLabel>
													<FormControl>
														<div className="relative">
															<Input
																{...field}
																list={`relationship-types-${path}`}
																placeholder={t(
																	'placeholders.relationshipType'
																)}
															/>
															<datalist id={`relationship-types-${path}`}>
																<option value="Subsidiary" />
																<option value="Partner" />
																<option value="Employee" />
																<option value="Authorized User" />
															</datalist>
														</div>
													</FormControl>
													<FormMessage />
												</FormItem>
											)}
										/>
									)
								},
								{
									name: 'notes',
									colSpan: 'col-span-full',
									render: ({
										form,
										path,
										isNewItem: _isNewItem
									}) => (
										<FormField
											control={form.control}
											name={`${path}.notes` as any}
											render={({ field }) => (
												<FormItem>
													<FormLabel>
														{t('fields.notes')}
													</FormLabel>
													<FormControl>
														<Textarea
															{...field}
															placeholder={t(
																'placeholders.beneficiaryNotes'
															)}
															rows={2}
														/>
													</FormControl>
													<FormMessage />
												</FormItem>
											)}
										/>
									)
								}
							]}
						/>
					</div>
				)}

				<div className="space-y-4">
					<div className="flex items-start justify-between">
						<div className="flex flex-col gap-2">
							<h3 className="text-lg font-semibold">
								{t('form.sections.vehicles.title')}
							</h3>
							<p className="text-sm text-muted-foreground">
								{t('form.sections.vehicles.description')}
							</p>
						</div>
					</div>
                                        <FieldList
                                                form={form}
                                                name="vehicles"
                                                title=""
                                                description=""
                                                emptyMessage={t('form.sections.vehicles.emptyMessage')}
                                                idField="vehicleId"
                                                defaultItem={{
                                                        plateNumber: '',
                                                        vehicleTypeId: defaultVehicleTypeId,
                                                        make: '',
                                                        model: '',
                                                        color: '',
                                                        isActive: true,
                                                        isDeleted: false
                                                }}
                                                addButtonLabel={t('fields.addNewVehicle')}
                                                renderHeaderActions={({ form, path }) => (
							<FormSwitch
								control={form.control}
								name={`${path}.isActive` as any}
								variant="compact"
								label={t('fields.active')}
							/>
						)}
						fields={[
							{
								name: 'plateNumber',
								isKeyField: true,
								render: ({ form, path, isNewItem }) => (
									<FormField
										control={form.control}
										name={`${path}.plateNumber` as any}
										render={({ field }) => (
											<FormItem>
												<FormLabel className="flex items-center gap-2">
													{isNewItem && (
														<Plus className="w-4 h-4 text-muted-foreground" />
													)}
													{isNewItem
														? t('fields.addNewVehicle')
														: t('fields.vehiclePlateNumber')}
												</FormLabel>
												<FormControl>
													<Input
														{...field}
														placeholder={t(
															'placeholders.vehiclePlateNumber'
														)}
													/>
												</FormControl>
												<FormMessage />
											</FormItem>
										)}
									/>
								)
							},
							{
								name: 'vehicleTypeId',
								render: ({ form, path, isNewItem: _isNewItem }) => (
									<FormField
										control={form.control}
										name={`${path}.vehicleTypeId` as any}
										render={({ field }) => (
											<FormItem>
												<FormLabel>
													{t('fields.vehicleType')}
												</FormLabel>
												<FormControl>
													<SentryDropdown
														value={field.value}
														onChange={field.onChange}
														options={vehicleTypeOptions}
														placeholder={t(
															'placeholders.vehicleType'
														)}
													/>
												</FormControl>
												<FormMessage />
											</FormItem>
										)}
									/>
								)
							},
							{
								name: 'make',
								render: ({ form, path, isNewItem: _isNewItem }) => (
									<FormField
										control={form.control}
										name={`${path}.make` as any}
										render={({ field }) => (
											<FormItem>
												<FormLabel>
													{t('fields.vehicleMake')}
												</FormLabel>
												<FormControl>
													<VehicleMakeAutocomplete
														value={field.value ?? ''}
														onChange={(value) => {
															field.onChange(value);
															// Reset model when make changes
															form.setValue(`${path}.model` as any, '');
														}}
														placeholder={t(
															'placeholders.vehicleMake'
														)}
													/>
												</FormControl>
												<FormMessage />
											</FormItem>
										)}
									/>
								)
							},
							{
								name: 'model',
								render: ({ form, path, isNewItem: _isNewItem }) => {
									const makeValue = form.watch(`${path}.make` as any);
									return (
										<FormField
											control={form.control}
											name={`${path}.model` as any}
											render={({ field }) => (
												<FormItem>
													<FormLabel>
														{t('fields.vehicleModel')}
													</FormLabel>
													<FormControl>
														<VehicleModelAutocomplete
															value={field.value ?? ''}
															onChange={field.onChange}
															makeName={makeValue || null}
															placeholder={t(
																'placeholders.vehicleModel'
															)}
														/>
													</FormControl>
													<FormMessage />
												</FormItem>
											)}
										/>
									);
								}
							},
							{
								name: 'color',
								render: ({ form, path, isNewItem: _isNewItem }) => (
									<FormField
										control={form.control}
										name={`${path}.color` as any}
										render={({ field }) => (
											<FormItem>
												<FormLabel>
													{t('fields.vehicleColor')}
												</FormLabel>
												<FormControl>
													<Input
														{...field}
														placeholder={t(
															'placeholders.vehicleColor'
														)}
													/>
												</FormControl>
												<FormMessage />
											</FormItem>
										)}
									/>
								)
							}
					]}
				/>
				</div>
			</form>
		</Form>
		</>
	);
};
